import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { captureExternalReturnUrl, getExternalFallbackUrl } from '../utils/externalSession';

/**
 * OAuth callback / validate page (blank layout, no sidebar).
 * Reads ?code= from URL, calls authenticateWithCode; on success redirects
 * into the app. On most failures (missing/invalid/expired code), this is
 * always an OAuth-flow attempt, so it redirects straight back to the
 * external platform — never shows this app's login UI or any "log in"
 * wording, and never requires a click. The one exception is a token that
 * exchanges fine but has no matching account on our side — that's shown as
 * a clean, specific message instead of a silent bounce, since redirecting
 * back without explanation would leave the user with no idea what's wrong.
 */
const Validate = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { authenticateWithCode } = useAuth();
  const authRunRef = useRef(false);
  const [accountNotFound, setAccountNotFound] = useState(false);

  useEffect(() => {
    if (authRunRef.current) return;
    authRunRef.current = true;

    const capturedReturnUrl = captureExternalReturnUrl(searchParams);

    const sendBackToExternalApp = () => {
      window.location.href = capturedReturnUrl || getExternalFallbackUrl();
    };

    const code = searchParams.get('code')?.toString();
    const run = async () => {
      localStorage.removeItem('ai_tech_api_key');
      if (!code) {
        sendBackToExternalApp();
        return;
      }
      const result = await authenticateWithCode(code);
      if (result?.ok) {
        navigate('/', { replace: true });
        window.location.reload();
      } else if (result?.reason === 'account_not_found') {
        setAccountNotFound(true);
      } else {
        sendBackToExternalApp();
      }
    };

    run();
  }, [searchParams, authenticateWithCode, navigate]);

  if (accountNotFound) {
    return (
      <section className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light">
        <div className="text-center p-4" style={{ maxWidth: 420 }}>
          <div className="mb-4">
            <img src="/logo.png" height={50} alt="Logo" />
          </div>
          <h2 className="h5 mb-2">We couldn't find your account</h2>
          <p className="text-muted mb-0">
            This account isn't set up in the system yet. Please contact your administrator to get access.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light">
      <div className="text-center p-4">
        <div className="mb-4">
          <img src="/logo.png" height={50} alt="Logo" />
        </div>
        <div className="d-flex justify-content-center gap-2">
          <div className="rounded-circle bg-primary opacity-75" style={{ width: 12, height: 12, animation: 'pulse 1s ease-in-out infinite' }} />
          <div className="rounded-circle bg-primary opacity-75" style={{ width: 12, height: 12, animation: 'pulse 1s ease-in-out 0.2s infinite' }} />
          <div className="rounded-circle bg-primary opacity-75" style={{ width: 12, height: 12, animation: 'pulse 1s ease-in-out 0.4s infinite' }} />
        </div>
        <p className="small text-muted mt-3 mb-0">Validating...</p>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }
      `}</style>
    </section>
  );
};

export default Validate;
